import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Alert,
    AlertTitle,
    Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { EquipmentRequestStatusEnum } from 'graphql/graphQlApiHooks';
import { getLabelStatusRequest } from '../../lib/getLabelStatusRequest';

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (comment: string) => void;
    newStatus: EquipmentRequestStatusEnum | null;
    selectedCount: number;
    loading: boolean;
}

export const BulkStatusChangeDialog: React.FC<Props> = ({
    open,
    onClose,
    onConfirm,
    newStatus,
    selectedCount,
    loading
}) => {
    const [comment, setComment] = useState('');

    const handleClose = () => {
        setComment('');
        onClose();
    };

    const handleConfirm = () => {
        onConfirm(comment);
        setComment('');
    };

    const warningMessage = () => {
        switch (newStatus) {
            case EquipmentRequestStatusEnum.Rejected:
                return `Вы собираетесь отклонить ${selectedCount} заявок. Это действие нельзя будет отменить.`;
            case EquipmentRequestStatusEnum.Acceptance:
                return <div>
                    {`Установка статуса `}
                    <strong>{getLabelStatusRequest(newStatus)}</strong>{` для ${selectedCount} заявок является необратимым действием.`}
                    <br/>
                    {`Вернуться на предыдущие шаги будет невозможно.`}
                </div>;
            case EquipmentRequestStatusEnum.EquipmentAccepted:
                return <div>
                    {`Установка статуса `}
                    <strong>{getLabelStatusRequest(newStatus)}</strong>{` для ${selectedCount} заявок является необратимым действием.`}
                    <br/>
                    {`Дальнейшие изменения заявок будут недоступны.`}
                </div>;
            default:
                return <div>
                    Вы уверены, что хотите изменить статус для {selectedCount} выбранных заявок?<br/>
                    {`Новый статус: `} <strong>{newStatus ? getLabelStatusRequest(newStatus) : ''}</strong>
                </div>;
        }
    };

    const isWarningStatus = newStatus === EquipmentRequestStatusEnum.Rejected
        || newStatus === EquipmentRequestStatusEnum.Acceptance
        || newStatus === EquipmentRequestStatusEnum.EquipmentAccepted;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Изменение статуса заявок</DialogTitle>
            <DialogContent>
                {isWarningStatus ? (
                    <Alert variant="standard" severity="error">
                        <AlertTitle>Внимание!</AlertTitle>
                        {warningMessage()}
                    </Alert>
                ) : (
                    <Alert variant="standard" severity="info">
                        {warningMessage()}
                    </Alert>
                )}

                <Box mt={2}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Для подтверждения изменения статуса заявок, при необходимости добавьте комментарий и нажмите
                        кнопку "Подтвердить".
                    </Typography>
                    <TextField
                        multiline
                        rows={4}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        fullWidth
                        size="small"
                        label="Комментарий"
                        placeholder="Например: Заявки согласованы и переданы дальше, для формирования пакета документов."
                        variant="filled"
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                fontSize: 12,
                            }
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">Отмена</Button>
                <LoadingButton 
                    loading={loading} 
                    onClick={handleConfirm} 
                    color="secondary" 
                    disabled={loading}
                >
                    Подтвердить
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};